import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import type { TaskWithRelations } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { canResolveReferralDocument, formatPatientType } from '@/hooks/use-referrals';
import { useSchedulingScope } from '@/scheduling/hooks/use-scheduling-scope';
import { SchedulingScopeFields } from '@/scheduling/components/SchedulingScopeFields';
import { SmsChat } from '@/pages/sms/test/SmsChat';
import { useSmsChat } from '@/pages/sms/test/use-sms-chat';

interface TextToScheduleDrawerProps {
  task: TaskWithRelations;
}

/**
 * Action drawer sibling to the "Call to schedule" voice drawer: starts a REAL
 * outbound SMS scheduling conversation for this referral's patient and drives it
 * turn-by-turn in a sheet (no text is sent — messages render here). Shares the
 * provider/appointment-type/location scope (useSchedulingScope) with the call
 * drawer and the /sms/test tool. Booking is against the real API/EHR.
 */
export function TextToScheduleDrawer({ task }: TextToScheduleDrawerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const couldResolveDocument = canResolveReferralDocument(task);
  const scope = useSchedulingScope(task);
  const chat = useSmsChat();

  // Fresh conversation on each open→close cycle so reopening starts clean.
  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) chat.reset();
  };

  const handleStart = (): void => {
    chat.start({
      mode: 'referral',
      taskId: task.id,
      providerId: scope.providerId,
      locationId: scope.locationId,
      appointmentTypeId: scope.appointmentTypeId,
      isNewPatient: formatPatientType(task.labels) === 'New',
    });
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={!couldResolveDocument}
        title={
          couldResolveDocument
            ? 'Start an SMS conversation to schedule an appointment'
            : 'This task has no document source — open a referral task to start a text'
        }
      >
        <MessageSquare className="mr-1.5 h-4 w-4" />
        Text to schedule
      </Button>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Text to schedule appointment</SheetTitle>
            <SheetDescription>
              Real SMS scheduling agent, driven from here (no text is sent). Books a real
              appointment against the linked referral.
            </SheetDescription>
          </SheetHeader>

          {chat.started ? (
            <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
              <SmsChat
                messages={chat.messages}
                isLoading={chat.isLoading}
                done={chat.done}
                onSend={chat.sendMessage}
                onReset={chat.reset}
              />
            </div>
          ) : (
            <>
              <SheetBody>
                <div className="flex flex-col gap-4 py-4">
                  <SchedulingScopeFields scope={scope} idPrefix="sms" />

                  {chat.startError && (
                    <div className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-sm">
                      {chat.startError}
                    </div>
                  )}
                </div>
              </SheetBody>
              <SheetActions>
                <SheetCancel />
                <Button
                  onClick={handleStart}
                  disabled={!scope.ready || chat.isLoading}
                  title={
                    scope.ready
                      ? undefined
                      : 'Pick a provider, appointment type, and location first'
                  }
                >
                  <MessageSquare className="mr-1.5 h-4 w-4" />
                  {chat.isLoading ? 'Starting…' : 'Start conversation'}
                </Button>
              </SheetActions>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
